import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function TrialEndedBanner() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "company") return;
    let alive = true;
    api.get("/company/overview")
      .then(({ data }) => { if (alive) setEnded(!!data.trial_ended); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  if (!ended) return null;
  return (
    <div data-testid="trial-ended-banner"
      className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3.5 text-sm text-amber-200">
      <Lock className="w-5 h-5 shrink-0 mt-0.5 text-amber-400" />
      <p className="leading-relaxed">{t("dash.trialEndedBanner")}</p>
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, sub, accent = "#06b6d4", testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d14] p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-400">{label}</span>
        <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <p className="mt-3 font-display font-bold text-3xl text-white">{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

export function PeriodSelect({ value, onChange, custom, setCustom }) {
  const { t } = useI18n();
  const periods = [
    { k: "today", l: t("dash.p_today") }, { k: "7d", l: t("dash.p_7d") }, { k: "30d", l: t("dash.p_30d") },
    { k: "3m", l: t("dash.p_3m") }, { k: "year", l: t("dash.p_year") }, { k: "all", l: t("dash.p_all") }, { k: "custom", l: t("dash.p_custom") },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="period-select">
      {periods.map((p) => (
        <button key={p.k} onClick={() => onChange(p.k)} data-testid={`period-${p.k}`}
          className={`px-3.5 py-1.5 rounded-full text-sm transition-colors duration-300 ${value === p.k ? "bg-brand-gradient text-white" : "glass text-zinc-400 hover:text-white"}`}>
          {p.l}
        </button>
      ))}
      {value === "custom" && (
        <div className="flex items-center gap-2">
          <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid="period-start" />
          <span className="text-zinc-500">→</span>
          <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid="period-end" />
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status }) {
  const { t } = useI18n();
  const map = {
    active: "text-emerald-400 bg-emerald-500/15", expiring: "text-amber-400 bg-amber-500/15",
    expired: "text-red-400 bg-red-500/15", inactive: "text-zinc-400 bg-white/10",
    suspended: "text-orange-400 bg-orange-500/15", archived: "text-zinc-400 bg-white/10",
    free: "text-cyan-300 bg-cyan-500/15", trial: "text-cyan-300 bg-cyan-500/15",
  };
  const cls = map[status] || map.inactive;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{t(`dash.${status}`)}</span>;
}

export function PaymentBadge({ status, testid }) {
  const { t } = useI18n();
  const map = {
    active: "text-emerald-400 bg-emerald-500/15",
    awaiting_payment: "text-cyan-300 bg-cyan-500/15",
    past_due: "text-amber-400 bg-amber-500/15",
    failed: "text-red-400 bg-red-500/15",
    canceled: "text-zinc-400 bg-white/10",
    manual: "text-zinc-400 bg-white/10",
  };
  const cls = map[status] || map.manual;
  return <span data-testid={testid} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{t(`dash.pm_${status}`)}</span>;
}

export function TrialBadge({ state, days }) {
  const { t } = useI18n();
  const map = {
    active: "text-emerald-400 bg-emerald-500/15",
    trial_active: "text-cyan-300 bg-cyan-500/15",
    trial_ended: "text-amber-400 bg-amber-500/15",
    preparation: "text-zinc-400 bg-white/10",
  };
  const label = {
    active: t("dash.trialActive"),
    trial_active: t("dash.trialOngoing"),
    trial_ended: t("dash.trialEnded"),
    preparation: t("dash.trialPrep"),
  };
  const cls = map[state] || map.preparation;
  const suffix = state === "trial_active" && days != null ? ` · ${days}j` : "";
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{(label[state] || label.preparation) + suffix}</span>;
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
    </div>
  );
}
